import React, { useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import CardHeader from '@material-ui/core/CardHeader'
import Grid from '@material-ui/core/Grid'
import FormControl from '@material-ui/core/FormControl'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import ArrowUpward from '@material-ui/icons/ArrowUpward'
import ArrowDownward from '@material-ui/icons/ArrowDownward'
import { Pie, Bar } from 'react-chartjs-2'
import componentStyles from 'assets/theme/components/cards/charts/card-affiliate-traffic.js'
import { PROJECTS, CATEGORIES } from 'components/admin/ExpenseModal'
import { CHART_PALETTE, paletteColor } from 'config/constants/ChartPalette'

// eslint-disable-next-line no-unused-vars
const useStyles = makeStyles(componentStyles)

const PROJECT_COLORS = PROJECTS.map((_, idx) => paletteColor(idx))
const CATEGORY_COLORS = CHART_PALETTE
const PAYER_COLORS = [paletteColor(0), paletteColor(3), paletteColor(2)]
const TYPE_LABELS = ['Wages', 'Contractor', 'Operating']

function fmt(amount) {
    return '€' + (Math.round((amount || 0) * 100) / 100).toLocaleString('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function pctDelta(current, previous) {
    if (!previous || previous === 0) return null
    return ((current - previous) / previous) * 100
}

function DeltaBadge({ delta }) {
    if (delta === null || delta === undefined || isNaN(delta)) return null
    const positive = delta > 0
    const negative = delta < 0
    const color = positive ? '#f5365c' : negative ? '#2dce89' : '#8898aa'
    // Note: for expenses, going up is bad (red), going down is good (green)
    const Icon = positive ? ArrowUpward : negative ? ArrowDownward : null
    return (
        <Box component='span' display='inline-flex' alignItems='center' style={{ color, fontSize: '0.75rem', marginLeft: '0.5rem' }}>
            {Icon && <Icon style={{ fontSize: '0.9rem' }} />}
            {Math.abs(delta).toFixed(1)}% MoM
        </Box>
    )
}

function StatCard({ title, value, delta, subValue, valueSize }) {
    return (
        <Card style={{ height: '100%' }}>
            <CardContent>
                <Box component='div' style={{ textTransform: 'uppercase', fontSize: '0.7rem', color: '#8898aa', letterSpacing: '0.5px', fontWeight: 600 }}>
                    {title}
                </Box>
                <Box component='div' marginTop='0.4rem' fontWeight={600} fontSize={valueSize || '1.4rem'} color='#32325d'>
                    {value}
                    <DeltaBadge delta={delta} />
                </Box>
                {subValue && (
                    <Box component='div' marginTop='0.5rem' fontSize='0.825rem' color='#525f7f'>
                        {subValue}
                    </Box>
                )}
            </CardContent>
        </Card>
    )
}

function aggregateByKey(entries, keyFn) {
    const totals = {}
    entries.forEach((e) => {
        const k = keyFn(e)
        if (!k) return
        totals[k] = (totals[k] || 0) + (e.amount || 0)
    })
    return totals
}

function aggregateByProject(entries) {
    const totals = PROJECTS.reduce((acc, p) => { acc[p] = 0; return acc }, {})
    entries.forEach((e) => {
        const split = e.split || {}
        const amount = e.amount || 0
        PROJECTS.forEach((p) => {
            const pct = parseFloat(split[p]) || 0
            totals[p] += (amount * pct) / 100
        })
    })
    return totals
}

function aggregateCategoryForProject(entries, project) {
    const totals = {}
    entries.forEach((e) => {
        const pct = parseFloat((e.split || {})[project]) || 0
        if (pct <= 0) return
        const alloc = ((e.amount || 0) * pct) / 100
        const cat = e.category || 'Other'
        totals[cat] = (totals[cat] || 0) + alloc
    })
    return totals
}

function ExpensesInsights({ entries, previousEntries }) {
    const [projectCategory, setProjectCategory] = useState('All')
    const [focusProject, setFocusProject] = useState('All')

    const totalSpend = useMemo(() => entries.reduce((s, e) => s + (e.amount || 0), 0), [entries])
    const prevTotalSpend = useMemo(() => previousEntries.reduce((s, e) => s + (e.amount || 0), 0), [previousEntries])

    const byType = useMemo(() => aggregateByKey(entries, (e) => e.type), [entries])
    const byPayer = useMemo(() => aggregateByKey(entries, (e) => e.payer), [entries])
    const byCategory = useMemo(() => aggregateByKey(entries, (e) => e.category), [entries])
    const byProject = useMemo(() => aggregateByProject(entries), [entries])
    const prevByProject = useMemo(() => aggregateByProject(previousEntries), [previousEntries])

    const filteredEntries = useMemo(() => (
        projectCategory === 'All' ? entries : entries.filter((e) => e.category === projectCategory)
    ), [entries, projectCategory])
    const filteredPrevEntries = useMemo(() => (
        projectCategory === 'All' ? previousEntries : previousEntries.filter((e) => e.category === projectCategory)
    ), [previousEntries, projectCategory])
    const filteredByProject = useMemo(() => aggregateByProject(filteredEntries), [filteredEntries])
    const filteredPrevByProject = useMemo(() => aggregateByProject(filteredPrevEntries), [filteredPrevEntries])

    // Category breakdown for a single focused project
    const categoryForProject = useMemo(() => (
        focusProject === 'All' ? {} : aggregateCategoryForProject(entries, focusProject)
    ), [entries, focusProject])
    const prevCategoryForProject = useMemo(() => (
        focusProject === 'All' ? {} : aggregateCategoryForProject(previousEntries, focusProject)
    ), [previousEntries, focusProject])

    const totalDelta = pctDelta(totalSpend, prevTotalSpend)

    const payerLabels = Object.keys(byPayer)
    const payerData = {
        labels: payerLabels.map((l) => `${l} (${fmt(byPayer[l])})`),
        datasets: [{
            data: payerLabels.map((l) => byPayer[l]),
            backgroundColor: PAYER_COLORS
        }]
    }

    const categoryLabels = Object.keys(byCategory)
    const categoryData = {
        labels: categoryLabels.map((l) => `${l} (${fmt(byCategory[l])})`),
        datasets: [{
            data: categoryLabels.map((l) => byCategory[l]),
            backgroundColor: CATEGORY_COLORS
        }]
    }

    const projectBarData = {
        labels: PROJECTS,
        datasets: [{
            label: 'Project allocation (€)',
            data: PROJECTS.map((p) => Math.round(filteredByProject[p] * 100) / 100),
            backgroundColor: PROJECT_COLORS,
            maxBarThickness: 60
        }]
    }

    // Categories present for the focused project (preserve defined order)
    const categoryForProjectLabels = CATEGORIES.filter((c) => (categoryForProject[c] || 0) > 0)
    const categoryForProjectBarData = {
        labels: categoryForProjectLabels,
        datasets: [{
            label: `${focusProject} spend by category (€)`,
            data: categoryForProjectLabels.map((c) => Math.round(categoryForProject[c] * 100) / 100),
            backgroundColor: categoryForProjectLabels.map((_, idx) => CATEGORY_COLORS[idx % CATEGORY_COLORS.length]),
            maxBarThickness: 60
        }]
    }

    const pieOptions = {
        legend: { display: true, position: 'bottom' },
        responsive: true,
        maintainAspectRatio: false
    }

    const barOptions = {
        legend: { display: false },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    fontColor: '#8898aa',
                    callback: (v) => '€' + v
                },
                gridLines: { color: '#e9ecef', zeroLineColor: '#e9ecef' }
            }],
            xAxes: [{
                ticks: { fontColor: '#525f7f', fontStyle: 'bold' },
                gridLines: { display: false }
            }]
        },
        tooltips: {
            callbacks: {
                label: (item) => fmt(item.yLabel)
            }
        }
    }

    const projectMomLines = PROJECTS.map((p) => {
        const d = pctDelta(filteredByProject[p], filteredPrevByProject[p])
        return { project: p, current: filteredByProject[p], delta: d }
    }).filter((row) => row.current > 0 || (row.delta !== null && row.delta !== 0))

    const categoryMomLines = categoryForProjectLabels.map((c) => {
        const d = pctDelta(categoryForProject[c], prevCategoryForProject[c])
        return { category: c, current: categoryForProject[c], delta: d }
    })

    const focusProjectTotal = categoryForProjectLabels.reduce((s, c) => s + (categoryForProject[c] || 0), 0)

    // Top project (post-split allocation)
    const topProject = PROJECTS
        .map((p) => ({ project: p, amount: byProject[p] || 0 }))
        .sort((a, b) => b.amount - a.amount)[0]
    const topProjectShare = totalSpend > 0 && topProject
        ? ((topProject.amount / totalSpend) * 100).toFixed(0)
        : 0

    return (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <StatCard
                        title='Total Spend'
                        value={fmt(totalSpend)}
                        delta={totalDelta}
                        subValue={
                            prevTotalSpend > 0
                                ? `vs ${fmt(prevTotalSpend)} last month`
                                : `${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`
                        }
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card style={{ height: '100%' }}>
                        <CardContent>
                            <Box style={{ textTransform: 'uppercase', fontSize: '0.7rem', color: '#8898aa', letterSpacing: '0.5px', fontWeight: 600 }}>
                                By Type
                            </Box>
                            <Box marginTop='0.6rem'>
                                {TYPE_LABELS.filter((t) => byType[t]).length === 0 ? (
                                    <Box fontSize='0.875rem' color='#8898aa'>—</Box>
                                ) : (
                                    TYPE_LABELS.filter((t) => byType[t]).map((t) => (
                                        <Box key={t} display='flex' justifyContent='space-between' marginBottom='0.25rem' fontSize='0.875rem'>
                                            <Box color='#525f7f'>{t}</Box>
                                            <Box color='#32325d' fontWeight={600}>{fmt(byType[t])}</Box>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <StatCard
                        title='Top Project'
                        value={topProject && topProject.amount > 0 ? `${topProject.project} · ${fmt(topProject.amount)}` : '—'}
                        subValue={topProject && topProject.amount > 0 ? `${topProjectShare}% of total spend` : 'No allocations'}
                    />
                </Grid>

                {payerLabels.length > 0 && (
                    <Grid item xs={12} md={6}>
                        <Card style={{ height: '100%' }}>
                            <CardHeader
                                subheader='By Payer'
                                subheaderTypographyProps={{ style: { fontSize: '1rem', fontWeight: 600, color: '#32325d' } }}
                            />
                            <CardContent>
                                <Box position='relative' height='300px'>
                                    <Pie data={payerData} options={pieOptions} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {categoryLabels.length > 0 && (
                    <Grid item xs={12} md={6}>
                        <Card style={{ height: '100%' }}>
                            <CardHeader
                                subheader='By Category'
                                subheaderTypographyProps={{ style: { fontSize: '1rem', fontWeight: 600, color: '#32325d' } }}
                            />
                            <CardContent>
                                <Box position='relative' height='300px'>
                                    <Pie data={categoryData} options={pieOptions} />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Card>
                        <CardHeader
                            subheader={
                                focusProject !== 'All'
                                    ? `${focusProject} · By Category (after % split)`
                                    : (projectCategory === 'All' ? 'By Project (after % split)' : `By Project · ${projectCategory} (after % split)`)
                            }
                            subheaderTypographyProps={{ style: { fontSize: '1rem', fontWeight: 600, color: '#32325d' } }}
                            action={
                                <Box display='flex' alignItems='center' style={{ marginTop: '0.25rem' }}>
                                    <FormControl variant='outlined' size='small' style={{ minWidth: 150, marginRight: '0.5rem' }}>
                                        <Select
                                            value={focusProject}
                                            onChange={(e) => setFocusProject(e.target.value)}
                                            style={{ fontSize: '0.85rem' }}
                                        >
                                            <MenuItem value='All'>All projects</MenuItem>
                                            {PROJECTS.map((p) => (
                                                <MenuItem key={p} value={p}>{p}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {focusProject === 'All' && (
                                        <FormControl variant='outlined' size='small' style={{ minWidth: 180 }}>
                                            <Select
                                                value={projectCategory}
                                                onChange={(e) => setProjectCategory(e.target.value)}
                                                style={{ fontSize: '0.85rem' }}
                                            >
                                                <MenuItem value='All'>All categories</MenuItem>
                                                {CATEGORIES.map((c) => (
                                                    <MenuItem key={c} value={c}>{c}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Box>
                            }
                        />
                        <CardContent>
                            {focusProject !== 'All' ? (
                                <>
                                    <Box position='relative' height='300px'>
                                        {categoryForProjectLabels.length > 0 ? (
                                            <Bar data={categoryForProjectBarData} options={barOptions} />
                                        ) : (
                                            <Box display='flex' alignItems='center' justifyContent='center' height='100%' color='#8898aa' fontSize='0.875rem'>
                                                No spend allocated to {focusProject} this month.
                                            </Box>
                                        )}
                                    </Box>
                                    {categoryMomLines.length > 0 && (
                                        <Box marginTop='1rem'>
                                            <Box marginBottom='0.5rem' fontSize='0.85rem' color='#32325d' fontWeight={600}>
                                                {focusProject} total: {fmt(focusProjectTotal)}
                                            </Box>
                                            <Box display='flex' flexWrap='wrap'>
                                                {categoryMomLines.map((row) => (
                                                    <Box key={row.category} marginRight='1.5rem' marginBottom='0.25rem' fontSize='0.85rem' color='#525f7f'>
                                                        <strong style={{ color: '#32325d' }}>{row.category}</strong> {fmt(row.current)}
                                                        <DeltaBadge delta={row.delta} />
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Box position='relative' height='300px'>
                                        <Bar data={projectBarData} options={barOptions} />
                                    </Box>
                                    {projectMomLines.length > 0 && (
                                        <Box marginTop='1rem' display='flex' flexWrap='wrap'>
                                            {projectMomLines.map((row) => (
                                                <Box key={row.project} marginRight='1.5rem' marginBottom='0.25rem' fontSize='0.85rem' color='#525f7f'>
                                                    <strong style={{ color: '#32325d' }}>{row.project}</strong> {fmt(row.current)}
                                                    <DeltaBadge delta={row.delta} />
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    )
}

export default ExpensesInsights
